/**
 * Deferred YouTube/Vimeo embed — inject iframe when near viewport.
 * @extends {HTMLElement}
 */
class VideoBlock extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {boolean} */
  #activated = false;

  connectedCallback() {
    if (this.#activated) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.#activate();
          break;
        }
      },
      { rootMargin: '200px 0px', threshold: 0 }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #activate() {
    if (this.#activated) return;

    const src = this.dataset.embedSrc;
    if (!src) return;

    const frame = this.querySelector('[data-video-frame]');
    const facade = this.querySelector('[data-video-facade]');
    if (!(frame instanceof HTMLElement)) return;

    this.#activated = true;
    this.#observer?.disconnect();
    this.#observer = null;

    const iframe = document.createElement('iframe');
    iframe.className = 'video-block__iframe';
    iframe.src = src;
    iframe.title = this.dataset.embedTitle || 'Video';
    iframe.allow = this.dataset.embedAllow || 'autoplay; fullscreen; picture-in-picture';
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');

    frame.hidden = false;
    frame.appendChild(iframe);

    if (facade instanceof HTMLElement) {
      facade.hidden = true;
    }

    this.classList.add('video-block__player--active');
  }
}

if (!customElements.get('video-block')) {
  customElements.define('video-block', VideoBlock);
}
